import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { 
    FindAllMerchantOffersAdminQueryQuery,
    FindAllMerchantOffersAdminQueryQueryVariables, 
    Offer 
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllMerchantOffersAdminQuery = gql`
  query FindAllMerchantOffersAdminQuery(
    $page: Int!
    $pageSize: Int!
    $id: String!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
  ) {
    findAllMerchantOffersAdmin(
      page: $page
      pageSize: $pageSize
      id: $id
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
    ) {
        data {
          id
          title
          offerType
          redemptions
          startDate
          endDate
          status
          createdAt
          createdBy {
            admin {
              id
              merchantProfile {
                id
                companyName
              }
            }
          }
        }
        total
        page
        pageSize
        totalPages
    }
  }
`;

export interface WithFindAllMerchantOffersAdmin {
    data: Offer[]
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreOffers: boolean;
}

interface Props {
  variables: FindAllMerchantOffersAdminQueryQueryVariables;
  children: (data: WithFindAllMerchantOffersAdmin) => React.ReactElement | null;
}

export const FindAllMerchantOffersAdminController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllMerchantOffersAdminQueryQuery,
    FindAllMerchantOffersAdminQueryQueryVariables
  >
  (findAllMerchantOffersAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllMerchantOffersAdminData = data?.findAllMerchantOffersAdmin?.data || [];
  const page = data?.findAllMerchantOffersAdmin?.page || variables.page;
  const pageSize = data?.findAllMerchantOffersAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllMerchantOffersAdmin?.total || 0;
  const totalPages = data?.findAllMerchantOffersAdmin?.totalPages || 0;
  const hasMoreOffers = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreOffers) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllMerchantOffersAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllMerchantOffersAdmin: {
            ...fetchMoreResult?.findAllMerchantOffersAdmin,
            data: [
              ...(prev?.findAllMerchantOffersAdmin?.data || []),
              ...(fetchMoreResult?.findAllMerchantOffersAdmin?.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreOffers]);

  return children({
    data: findAllMerchantOffersAdminData as any,
    loading,
    loadMore,
    hasMoreOffers,
    page,
    pageSize,
    total,
    totalPages,
  });
};